import React, { useContext, useEffect, useState } from "react";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import ViewIcon from "@material-ui/icons/Visibility";
import CustomTable, { CustomTableRowData } from "../../../components/CustomTable";
import { ProductContext } from "../../../contexts/ProductContext";
import formatIdentifierAsString from "../../../api/services/common/formatIdentifierAsString";
import { WithPredefinedChip } from "../../../components/Chip/PredefinedSampleChip";
import ProductSampleDTO from "../../../api/models/product/ProductSampleDTO";
import { CompetitorProductSample, ProductSampleReference } from "../../../api/models/product/ProductSampleReference";

function isCompetitorProductSampleReference(
  sampleRef: ProductSampleReference,
): sampleRef is ProductSampleReference<CompetitorProductSample> {
  return sampleRef.productSample.type === "COMPETITOR_PRODUCT";
}

function isCompetitorProductSample(sample: ProductSampleDTO): sample is CompetitorProductSample {
  return sample.type === "COMPETITOR_PRODUCT";
}
interface CompetitorProductSampleOverviewTableProps {
  onDelete: (competitorProductSample: Partial<CompetitorProductSample>) => void;
  onEdit: (
    productSampleId: number,
    additionalActions?: (sampleData: ProductSampleDTO, usedIn: boolean) => void,
  ) => void;
  productSampleReferences: ProductSampleReference[];
  showProductSampleDeleteAction: (data: { id: number }) => boolean;
  showProductSampleEditAction: (data: { id: number }) => boolean;
  showProductSampleViewDetailsAction: (data: { id: number }) => boolean;
}

const CompetitorProductSampleOverviewTable: React.FC<CompetitorProductSampleOverviewTableProps> = (props) => {
  const {
    onDelete,
    onEdit: handleEditById,
    productSampleReferences,
    showProductSampleDeleteAction,
    showProductSampleEditAction,
    showProductSampleViewDetailsAction,
  } = props;
  const [competitorProductSampleReferences, setCompetitorProductSampleReferences] = useState<
    ProductSampleReference<CompetitorProductSample>[]
  >([]);
  const { updateCompetitorCompany, updateCompetitorProduct } = useContext(ProductContext);

  const onEditAdditionalActions = (sampleData: ProductSampleDTO) => {
    if (isCompetitorProductSample(sampleData)) {
      updateCompetitorCompany({
        id: sampleData.competitorCompanyId,
        name: sampleData.competitorCompanyName,
      });
      updateCompetitorProduct({
        id: sampleData.competitorProductId,
        name: sampleData.competitorProductName,
      });
    }
  };

  const handleEdit = (rowData: CustomTableRowData) => {
    if (showProductSampleEditAction(rowData) || showProductSampleViewDetailsAction(rowData)) {
      handleEditById(rowData.id, onEditAdditionalActions);
    }
  };

  useEffect(() => {
    const newSampleRefs = productSampleReferences.filter(
      (psr: ProductSampleReference): psr is ProductSampleReference<CompetitorProductSample> =>
        isCompetitorProductSampleReference(psr),
    );
    if (JSON.stringify(newSampleRefs) !== JSON.stringify(competitorProductSampleReferences)) {
      setCompetitorProductSampleReferences(newSampleRefs);
    }
  }, [productSampleReferences]);

  if (competitorProductSampleReferences.length === 0) {
    return <></>;
  }
  return (
    <CustomTable
      boldIndex={0}
      selectedItemNameValue="productSampleName"
      actionColumnIndexes={[0]}
      excludedKeys={["id", "usedIn"]}
      hasRowNumbers
      hasFixedColumns
      handleEdit={handleEdit}
      header="Competitor Product Samples"
      columns={["Product Sample Name", "Product Name", "Company", "Delivered by"]}
      rowMenuActions={[
        {
          action: (rowData): void => handleEditById(rowData.id, onEditAdditionalActions),
          icon: <EditIcon />,
          text: "Edit",
          show: showProductSampleEditAction,
        },
        {
          action: (rowData): void => handleEditById(rowData.id, onEditAdditionalActions),
          icon: <ViewIcon />,
          text: "View Details",
          show: (data) => !showProductSampleEditAction(data),
        },
        {
          action: (rowData): void => onDelete(rowData),
          icon: <DeleteIcon />,
          text: "Delete",
          show: showProductSampleDeleteAction,
        },
      ]}
      rowMenus
      rows={competitorProductSampleReferences.map((psr, index: number) => {
        const { usedIn, productSample } = psr;
        return {
          productSampleName: usedIn ? (
            <WithPredefinedChip text={productSample.productSampleName} />
          ) : (
            productSample.productSampleName
          ),
          productName: productSample.competitorProductName,
          company: productSample.competitorCompanyName,
          deliveredBy: formatIdentifierAsString(productSample.deliveredBy),
          id: productSample.id,
          rowNumber: index + 1,
          usedIn,
        };
      })}
    />
  );
};

export default CompetitorProductSampleOverviewTable;
